import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Trash2 } from 'lucide-react';
import { leadApi, taskApi } from '../services/api';
import { Activity, Lead, LeadEngagement, Task, Understanding, User } from '../types';
import { fmtDayTime, fmtGap, isLate, label } from '../lib';
import { useCallWindows } from '../context/CallWindowsContext';
import { Failed, Loading, StageChip } from '../components/WorkBits';
import WhatsAppChat from '../components/WhatsAppChat';
import SiteVisitModal from '../components/SiteVisitModal';
import CallModal from '../components/CallModal';
import AdvisorPanel from '../components/AdvisorPanel';
import StagePanel from '../components/StagePanel';
import Sheet from '../components/lead/Sheet';
import { DiscoveryCard, IdentityCard, PublicRecordsCard, TagsCard, UnderstandingCard } from '../components/lead/Profile';
import Timeline from '../components/lead/Timeline';
import {
  AlbumCard, EngagementCard, NoteCard, ScheduleSheet, SuggestionCard, TaskSheet, UpcomingCard,
} from '../components/lead/Work';
import ShareSheet from '../components/lead/ShareSheet';
import { hasEnrichment, interestOf } from '../components/lead/leadModel';

/** The lead page, in three columns: who they are · what happened · what to do
 *  next. Everything the Nurture app's lead profile shows lives here too; the
 *  sections are in components/lead/. */
const LeadDetail: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const windows = useCallWindows();

  const [lead, setLead] = useState<Lead | null>(null);
  const [activity, setActivity] = useState<Activity[]>([]);
  // Every task on the lead: open ones are the work, proposed ones the AI's
  // suggestions, closed ones feed the timeline.
  const [tasks, setTasks] = useState<Task[]>([]);
  const [understanding, setUnderstanding] = useState<Understanding | null>(null);
  const [engagement, setEngagement] = useState<LeadEngagement | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [leadConversationId, setLeadConversationId] = useState<string | null>(null);

  const [showWhatsAppChat, setShowWhatsAppChat] = useState(false);
  const [showSiteVisitModal, setShowSiteVisitModal] = useState(false);
  const [visitProposal, setVisitProposal] = useState<Task | null>(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showAdvisorPanel, setShowAdvisorPanel] = useState(false);
  const [showStagePanel, setShowStagePanel] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [reviewTask, setReviewTask] = useState<Task | null>(null);
  const [openTask, setOpenTask] = useState<Task | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [recordsSignal, setRecordsSignal] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const currentUser: User | null = (() => {
    try {
      const raw = localStorage.getItem('touchpoint_portal_user');
      return raw ? (JSON.parse(raw) as User) : null;
    } catch {
      return null;
    }
  })();

  const load = useCallback(async (initial: boolean) => {
    if (!leadId) return;
    if (initial) setLoading(true);
    try {
      const [leadData, activityData, taskData, understandingData, engagementData] = await Promise.all([
        leadApi.get(leadId),
        leadApi.getActivity(leadId),
        taskApi.listForLead(leadId),
        leadApi.getUnderstanding(leadId).catch(() => null),
        leadApi.getEngagement(leadId).catch(() => null),
      ]);
      setLead(leadData);
      setActivity(activityData.activity || []);
      setTasks(taskData.tasks);
      setUnderstanding(understandingData);
      setEngagement(engagementData);
      if (leadData.conversation_id) setLeadConversationId(leadData.conversation_id);
      setLoadError(null);
    } catch (error: any) {
      setLoadError(error?.message || 'failed to load');
    }
    if (initial) setLoading(false);
  }, [leadId]);

  const refresh = useCallback(() => load(false), [load]);
  useEffect(() => { load(true); }, [load]);


  if (loading) return <div className="px-7 py-6"><Loading what="the lead" /></div>;
  if (!lead) return <div className="px-7 py-6"><Failed what="the lead" error={loadError || 'not found'} /></div>;

  const openTasks = tasks.filter((t) => t.status === 'open');
  const proposed = tasks.filter((t) => t.status === 'proposed');
  const visitTask = openTasks.find((t) => t.type === 'site_visit') || null;

  const openVisit = (proposal: Task | null = null) => { setVisitProposal(proposal); setShowSiteVisitModal(true); };
  const review = (t: Task) => (t.type === 'site_visit' ? openVisit(t) : setReviewTask(t));

  const saveName = async () => {
    if (!renaming?.trim()) return;
    try {
      await leadApi.update(lead.id, { name: renaming.trim() });
      setRenaming(null);
      refresh();
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || 'Rename failed');
    }
  };

  const handleDeleteLead = async () => {
    setDeleting(true);
    try {
      await leadApi.delete(lead.id);
      navigate('/leads');
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || 'Failed to delete the lead');
      setDeleting(false);
    }
  };

  /** The one thing to do next, and why — stated above the fold so the operator
   *  does not have to reconstruct it from the cards. Ordered by how close the
   *  lead is to a visit; the first match wins. */
  const nextBestAction = ((): { headline: string; why: string; cta: string; act: () => void } | null => {
    if (visitTask) {
      return {
        headline: `Site visit booked for ${fmtDayTime(visitTask.due_at)}`,
        why: 'Confirm the day before. An unconfirmed visit is the most common way one is lost.',
        cta: 'Open the visit',
        act: () => openVisit(),
      };
    }
    const t = engagement?.totals;
    if (t && (t.cta_clicked || t.brochure_opens > 0 || t.opens >= 3)) {
      return {
        headline: 'Ask for the visit. The signal is there.',
        why: [
          t.opens ? `${t.opens} link opens` : null,
          t.max_video_pct ? `${t.max_video_pct}% of a film watched` : null,
          t.brochure_opens ? 'brochure opened' : null,
          t.cta_clicked ? 'visit CTA clicked' : null,
        ].filter(Boolean).join(' · ') + ' — and no visit is on the books.',
        cta: 'Book the visit',
        act: () => openVisit(),
      };
    }
    if (lead.next_action_at && isLate(lead.next_action_at, windows)) {
      const kind = label(lead.next_action_type || 'action');
      return {
        headline: `${kind.charAt(0).toUpperCase()}${kind.slice(1)} is overdue`,
        why: `Due ${fmtDayTime(lead.next_action_at)} — ${fmtGap(lead.next_action_at)}.`,
        cta: 'Call now',
        act: () => setShowCallModal(true),
      };
    }
    if (!lead.last_activity_at) {
      return {
        headline: 'Nobody has reached this lead yet',
        why: 'No call, no message, no visit on record.',
        cta: 'Call now',
        act: () => setShowCallModal(true),
      };
    }
    return null;
  })();

  return (
    <div className="w-full px-7 pt-6 pb-16">
      <div className="page-head !items-start">
        <div className="flex items-start gap-3 min-w-0">
          <button className="icb flex-none" onClick={() => navigate('/leads')} title="Back to leads">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.9} />
          </button>
          <div className="min-w-0">
            <h1 className="page-title flex items-center gap-2 flex-wrap">
              {lead.name}
              {hasEnrichment(lead) && (
                <button type="button" className="chip chip-forest !text-[11px]" title="Matched to public records"
                  onClick={() => setRecordsSignal((n) => n + 1)}>
                  <ShieldCheck className="w-3 h-3" strokeWidth={2} />Enriched
                </button>
              )}
              <button type="button" className="text-[12px] font-sans font-normal text-graphite underline decoration-dotted"
                onClick={() => setRenaming(lead.name)}>rename</button>
            </h1>
            <p className="page-sub num flex items-center gap-2 flex-wrap">
              <span>{lead.mobile || lead.email}{lead.source && ` · ${lead.source}`}</span>
              {lead.next_action_at && (
                <span className={`chip ${isLate(lead.next_action_at, windows) ? 'chip-brick' : 'chip-gold'}`}>
                  Next: {label(lead.next_action_type || 'action')} · {fmtGap(lead.next_action_at)}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" title="Change stage & interest" onClick={() => setShowStagePanel(true)}>
            <StageChip stage={lead.stage} />
          </button>
          {interestOf(lead) && <span className="chip chip-forest">Interest · {interestOf(lead)}</span>}
        </div>
      </div>

      {actionError && (
        <div className="card !border-brick/40 mb-3.5 flex justify-between items-center text-brick text-sm">
          {actionError}<button onClick={() => setActionError(null)}>✕</button>
        </div>
      )}

      {nextBestAction && (
        <div className="card border-l-4 border-l-gold mb-3.5">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <b className="block text-[14.5px]">{nextBestAction.headline}</b>
              <span className="text-graphite text-[12.5px]">{nextBestAction.why}</span>
            </div>
            <button className="btn btn-pri" onClick={nextBestAction.act}>{nextBestAction.cta}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] min-[1280px]:grid-cols-[300px_minmax(0,1fr)_310px] gap-4 items-start">
        {/* Who they are */}
        <div className="space-y-3.5 min-w-0">
          <IdentityCard lead={lead}
            onCall={() => setShowCallModal(true)}
            onChat={() => setShowWhatsAppChat(true)}
            onPersonalWa={() => setShowShare(true)}
            onAdvisors={() => setShowAdvisorPanel(true)} />
          <DiscoveryCard lead={lead} />
          <UnderstandingCard understanding={understanding} />
          <PublicRecordsCard lead={lead} openSignal={recordsSignal} />
          <TagsCard lead={lead} onChanged={refresh} />
          <div className="pt-2">
            {confirmDelete ? (
              <div className="card !border-brick/40 text-sm">
                <p className="m-0 mb-2">Permanently delete {lead.name} and all their data? This can't be undone.</p>
                <div className="flex gap-2">
                  <button className="btn btn-sm" onClick={() => setConfirmDelete(false)}>Keep</button>
                  <button className="btn btn-sm !bg-brick !border-brick text-warm-white" disabled={deleting} onClick={handleDeleteLead}>
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} className="btn btn-sm !text-brick !border-brick/30 flex items-center gap-1.5">
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />Delete lead
              </button>
            )}
          </div>
        </div>

        {/* What happened — on narrower screens the work column stacks under it */}
        <div className="min-w-0 space-y-3.5 lg:row-span-2 min-[1280px]:row-span-1">
          <Timeline activity={activity} tasks={tasks} onOpenChat={() => setShowWhatsAppChat(true)} />
        </div>

        {/* What to do next */}
        <div className="space-y-3.5 min-w-0 lg:col-start-1 min-[1280px]:col-start-3 min-[1280px]:row-start-1">
          <SuggestionCard lead={lead} proposed={proposed} onReview={review} onChanged={refresh} />
          <UpcomingCard tasks={openTasks} windows={windows} onOpen={setOpenTask}
            onSchedule={() => setShowSchedule(true)} onBookVisit={() => openVisit()} />
          <EngagementCard engagement={engagement} />
          <AlbumCard engagement={engagement} />
          <NoteCard lead={lead} onSaved={refresh} />
        </div>
      </div>

      {renaming !== null && (
        <Sheet title="Lead name" onClose={() => setRenaming(null)}
          footer={<><button className="btn" onClick={() => setRenaming(null)}>Cancel</button>
            <button className="btn btn-pri" disabled={!renaming.trim() || renaming.trim() === lead.name} onClick={saveName}>Save</button></>}>
          <input id="lead-name" className="inp w-full" autoFocus value={renaming}
            onChange={(e) => setRenaming(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveName()} />
        </Sheet>
      )}

      {showShare && lead.mobile && (
        <ShareSheet lead={lead} operatorName={currentUser?.name} operatorEmail={currentUser?.email} onClose={() => setShowShare(false)} onLogged={refresh} />
      )}

      {(showSchedule || reviewTask) && (
        <ScheduleSheet lead={lead} proposal={reviewTask}
          onClose={() => { setShowSchedule(false); setReviewTask(null); }} onChanged={refresh} />
      )}

      {openTask && (
        <TaskSheet task={openTask} onClose={() => setOpenTask(null)} onChanged={refresh} onOpenVisit={() => openVisit()} />
      )}

      {showWhatsAppChat && leadConversationId && (
        <WhatsAppChat
          conversationId={leadConversationId}
          title={lead.name}
          subtitle={lead.mobile || undefined}
          onClose={() => setShowWhatsAppChat(false)}
        />
      )}

      {showCallModal && (
        <CallModal lead={lead} onClose={() => setShowCallModal(false)} />
      )}

      {showSiteVisitModal && (
        <SiteVisitModal
          lead={lead}
          visitTask={visitTask}
          proposal={visitTask ? null : visitProposal}
          onClose={() => { setShowSiteVisitModal(false); setVisitProposal(null); }}
          onChanged={refresh}
        />
      )}

      {showStagePanel && leadId && (
        <StagePanel
          leadId={leadId}
          current={lead.stage}
          crmLabel={lead.crm_stage_label}
          interest={interestOf(lead)}
          onClose={() => { setShowStagePanel(false); refresh(); }}
        />
      )}

      {showAdvisorPanel && leadId && currentUser && (
        <AdvisorPanel
          leadId={leadId}
          currentUser={currentUser}
          onClose={() => {
            setShowAdvisorPanel(false);
            refresh();
          }}
        />
      )}
    </div>
  );
};

export default LeadDetail;
